import { Link } from "react-router-dom";
import { AdvisorLogoIcon } from "@/components/icons/AdvisorLogo";

// ─────────────────────────────────────────────────────────────────────────────
// Generic Terms of Service. Reachable publicly at /terms (no auth) so it can be
// used as the OAuth consent-screen "Terms of Service URL".
//
// TEMPLATE NOTICE: this is a reasonable, honest starting point, but it is NOT
// legal advice. Review the placeholders and have counsel look it over before you
// rely on it for a production launch.
// ─────────────────────────────────────────────────────────────────────────────
const COMPANY_NAME = "ADVisor";
const CONTACT_EMAIL = "support@ad-visor.dev";
const EFFECTIVE_DATE = "July 25, 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-bold text-neutral-900 mb-2">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-neutral-600">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="h-14 border-b border-neutral-200 bg-white flex items-center px-6">
        <Link to="/login" className="flex items-center gap-2.5">
          <AdvisorLogoIcon size="sm" className="text-steel-600" />
          <span className="font-bold text-sm text-steel-600 tracking-tight">{COMPANY_NAME}</span>
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-neutral-900">Terms of Service</h1>
        <p className="text-sm text-neutral-400 mt-1">Last updated: {EFFECTIVE_DATE}</p>

        <p className="mt-6 text-sm leading-relaxed text-neutral-600">
          These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the{" "}
          {COMPANY_NAME} application and related services (the &ldquo;Service&rdquo;). By accessing
          or using the Service, you agree to be bound by these Terms. If you do not agree, do not use
          the Service.
        </p>

        <Section title="1. Eligibility and Accounts">
          <p>
            You must be at least 16 years old and able to form a binding contract to use the Service.
            You are responsible for the activity that occurs under your account and for keeping your
            login credentials secure. You agree to provide accurate information and to keep it up to
            date.
          </p>
        </Section>

        <Section title="2. Use of the Service">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Use the Service in violation of any applicable law or regulation.</li>
            <li>
              Access accounts, data, or advertising platforms you are not authorized to access.
            </li>
            <li>
              Interfere with, disrupt, or attempt to gain unauthorized access to the Service or its
              systems.
            </li>
            <li>Reverse engineer, resell, or misuse the Service or its content.</li>
            <li>Upload malicious code or use the Service to transmit unlawful content.</li>
          </ul>
        </Section>

        <Section title="3. Connected Third-Party Accounts">
          <p>
            The Service lets you connect third-party accounts (such as Google Ads, Google Analytics,
            or Meta). Your use of those services is governed by their own terms and policies. You are
            responsible for ensuring you have the rights and permissions to connect those accounts
            and for the data you make available through them. We are not responsible for third-party
            services or their availability.
          </p>
        </Section>

        <Section title="4. Human Oversight and Automated Features">
          <p>
            The Service includes automated and AI-assisted features that generate reports, insights,
            and recommendations. These are provided for informational purposes and may contain
            errors. You are responsible for reviewing and approving any action before it is taken,
            and for decisions you make based on the Service.
          </p>
        </Section>

        <Section title="5. Intellectual Property">
          <p>
            The Service, including its software, design, and content, is owned by {COMPANY_NAME} and
            protected by intellectual property laws. We grant you a limited, non-exclusive,
            non-transferable license to use the Service in accordance with these Terms. You retain
            ownership of the data and content you provide.
          </p>
        </Section>

        <Section title="6. Fees">
          <p>
            Some features may require payment. Where fees apply, they will be described at the point
            of purchase. Unless otherwise stated, fees are non-refundable except as required by law.
          </p>
        </Section>

        <Section title="7. Termination">
          <p>
            You may stop using the Service and delete your account at any time from Settings &rarr;
            Danger Zone. We may suspend or terminate your access if you violate these Terms or if we
            discontinue the Service. Upon termination, your right to use the Service ends, and we
            will handle your data as described in our Privacy Policy.
          </p>
        </Section>

        <Section title="8. Disclaimers">
          <p>
            The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
            warranties of any kind, whether express or implied, including warranties of
            merchantability, fitness for a particular purpose, and non-infringement. We do not
            warrant that the Service will be uninterrupted, error-free, or secure.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, {COMPANY_NAME} will not be liable for any
            indirect, incidental, special, consequential, or punitive damages, or for any loss of
            profits, revenue, data, or business, arising out of or related to your use of the
            Service.
          </p>
        </Section>

        <Section title="10. Changes to the Service and Terms">
          <p>
            We may modify the Service or these Terms from time to time. When we update these Terms,
            we will revise the &ldquo;Last updated&rdquo; date above. Continued use of the Service
            after changes take effect constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="11. Governing Law">
          <p>
            These Terms are governed by the laws of the jurisdiction in which {COMPANY_NAME}
            operates, without regard to conflict-of-law principles. Any disputes will be resolved in
            the courts of that jurisdiction.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            If you have questions about these Terms, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-steel-600 underline">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-neutral-200 flex items-center justify-between text-xs text-neutral-400">
          <span>
            &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </span>
          <Link to="/privacy" className="hover:text-neutral-600 underline">
            Privacy Policy
          </Link>
        </footer>
      </main>
    </div>
  );
}
